import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Firestore } from "firebase/firestore";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  runTransaction,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { fmtDateOnly } from "../lib/formatters";
import type { AppUser } from "../hooks/useAuthState";
import { toMillis, LOCK_STALE_MS } from "../components/editModalFunctions";

type Batch = {
  id: string;
  name: string;
  kolliCount?: number;
  createdAt?: any;
  createdBy?: string | null;
  status?: string;
  buyerId?: string | null;
  buyerName?: string | null;
  buyerContact?: string | null;
  trackingId?: string | null;
  invoiceNumber?: string | null;
  invoiceTotal?: number | null;
  paymentReceived?: number | null;
  creditNoteNumber?: string | null;
  creditAmount?: number | null;
  creditReason?: string | null;
};

type Buyer = { id: string; name: string; contact?: string };

type Props = {
  db: Firestore;
  batchId: string;
  user: AppUser | null;
  onOpenItem?: (id: string) => void;
  onClose?: () => void;
};

type ItemRow = {
  id: string;
  serial?: string;
  manufacturer?: string;
  model?: string;
  productType?: string;
  grade?: string;
  customer?: string;
  salesCpu?: string;
  salesStorage?: string;
  salesRam?: string;
  salesBatchId?: string;
  salesKolliId?: string | null;
  salesKolliName?: string | null;
  damageNotes?: string;
  lockedBy?: string | null;
  lockedAt?: any;
};

type Kolli = { id: string; name: string };

const STATUS_LABELS: Record<string, string> = {
  open: "Öppen",
  ready_to_ship: "Färdig att skicka",
  sold: "Såld",
  shipped: "Skickad",
  invoiced: "Fakturerad",
  paid: "Betalning mottagen",
  credited: "Kredit",
};

const STATUS_ORDER = ["open", "ready_to_ship", "sold", "shipped", "invoiced", "paid", "credited"] as const;

export default function BatchDetail({ db, batchId, user, onOpenItem, onClose }: Props): JSX.Element | null {
  const [batch, setBatch] = useState<Batch | null>(null);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [kollin, setKollin] = useState<Kolli[]>([]);
  const [items, setItems] = useState<ItemRow[]>([]);
  const originalKollinRef = React.useRef<Kolli[]>([]);
  const originalItemsRef = React.useRef<ItemRow[]>([]);
  const [newBuyerName, setNewBuyerName] = useState("");
  const [newBuyerContact, setNewBuyerContact] = useState("");
  const [showNewBuyer, setShowNewBuyer] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("open");
  const [statusError, setStatusError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Batch | null>(null);
  const [form, setForm] = useState<Partial<Batch>>({});
  const [itemLocks, setItemLocks] = useState<Record<string, { lockedBy: string | null; lockedAtMs: number | null }>>({});

  const computeDirty = useCallback((nextForm: Partial<Batch>, statusSel: string, saved: Batch | null): boolean => {
    if (!saved) return false; // ingen baseline ännu
    const normStr = (v: any) => (v === undefined || v === null ? "" : String(v));
    const normNum = (v: any) => (v === undefined || v === null ? null : Number(v));
    const fieldsStr: (keyof Batch)[] = ["buyerId", "buyerName", "buyerContact", "trackingId", "invoiceNumber", "creditNoteNumber", "creditReason"];
    const fieldsNum: (keyof Batch)[] = ["invoiceTotal", "paymentReceived", "creditAmount"];
    const diffStr = fieldsStr.some((k) => normStr((nextForm as any)[k]) !== normStr((saved as any)[k]));
    const diffNum = fieldsNum.some((k) => normNum((nextForm as any)[k]) !== normNum((saved as any)[k]));
    const diffStatus = statusSel !== ((saved as any).status || "open");
    return diffStr || diffNum || diffStatus;
  }, []);

  useEffect(() => {
    const loadBatch = async () => {
      const ref = doc(db, "salesBatches", batchId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const data = { id: snap.id, ...(snap.data() as any) };
      setBatch(data as Batch);
      setLastSaved(data as Batch);
      const dbStatus = (data as any).status || "open";
      const nextForm: Partial<Batch> = {
        buyerId: data.buyerId || "",
        buyerName: data.buyerName || "",
        buyerContact: data.buyerContact || "",
        trackingId: data.trackingId || "",
        invoiceNumber: data.invoiceNumber || "",
        invoiceTotal: data.invoiceTotal ?? null,
        paymentReceived: data.paymentReceived ?? null,
        creditAmount: data.creditAmount ?? null,
        creditNoteNumber: data.creditNoteNumber || "",
        creditReason: data.creditReason || "",
      };
      setSelectedStatus(dbStatus);
      setForm(nextForm);
      setDirty(false);
    };
    void loadBatch();
  }, [batchId, db]);

  useEffect(() => {
    const loadBuyers = async () => {
      const qs = await getDocs(query(collection(db, "salesCustomers"), orderBy("name")));
      setBuyers(qs.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as Buyer)));
    };
    void loadBuyers();
  }, [db]);

  useEffect(() => {
    const loadData = async () => {
      if (!batchId) return;
      const itemsSnap = await getDocs(query(collection(db, "itInventory"), where("salesBatchId", "==", batchId)));
      const itemRows = itemsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      setItems(itemRows);
      originalItemsRef.current = itemRows;
      const lockMap: Record<string, { lockedBy: string | null; lockedAtMs: number | null }> = {};
      for (const r of itemRows) {
        const lockedAtMs = toMillis((r as any).lockedAt);
        lockMap[r.id] = { lockedBy: (r as any).lockedBy ?? null, lockedAtMs };
      }
      setItemLocks(lockMap);

      const kolliSnap = await getDocs(query(collection(db, "salesBatches", batchId, "kollin"), orderBy("createdAt", "asc")));
      const kolliRows = kolliSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as Kolli));
      setKollin(kolliRows);
      originalKollinRef.current = kolliRows;
    };
    void loadData();
  }, [batchId, db]);

  // Live-uppdatera låsstatus så dropdownen aktiveras automatiskt när låset släpper
  useEffect(() => {
    if (!batchId) return;
    const q = query(collection(db, "itInventory"), where("salesBatchId", "==", batchId));
    const unsub = onSnapshot(q, (snap) => {
      const lockMap: Record<string, { lockedBy: string | null; lockedAtMs: number | null }> = {};
      snap.forEach((docSnap) => {
        const cur = docSnap.data() as any;
        lockMap[docSnap.id] = {
          lockedBy: cur.lockedBy ?? null,
          lockedAtMs: toMillis(cur.lockedAt),
        };
      });
      setItemLocks((prev) => ({ ...prev, ...lockMap }));
    });
    return () => unsub();
  }, [batchId, db]);

  const buyerMap = useMemo(() => Object.fromEntries(buyers.map((b) => [b.id, b])), [buyers]);
  const kolliNameById = useMemo(() => Object.fromEntries(kollin.map((k) => [k.id, k.name])), [kollin]);
  const isLockedByOther = useCallback((id: string): { locked: boolean; lockedBy: string | null } => {
    const me = user?.email ?? user?.uid ?? null;
    const entry = itemLocks[id];
    if (!entry) return { locked: false, lockedBy: null };
    const { lockedBy, lockedAtMs } = entry;
    const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
    if (lockedBy && lockedBy !== me && !isStale) return { locked: true, lockedBy };
    return { locked: false, lockedBy: null };
  }, [itemLocks, user]);

  const refreshLockForItem = useCallback(async (id: string): Promise<{ locked: boolean; lockedBy: string | null }> => {
    try {
      const ref = doc(db, "itInventory", id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return { locked: false, lockedBy: null };
      const cur = snap.data() as any;
      const lockedAtMs = toMillis(cur.lockedAt);
      setItemLocks((prev) => ({ ...prev, [id]: { lockedBy: cur.lockedBy ?? null, lockedAtMs } }));
      const me = user?.email ?? user?.uid ?? null;
      const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
      if (cur.lockedBy && cur.lockedBy !== me && !isStale) {
        return { locked: true, lockedBy: cur.lockedBy };
      }
      return { locked: false, lockedBy: null };
    } catch {
      return { locked: false, lockedBy: null };
    }
  }, [db, user]);

  if (!batch) return null;

  const savedStatus = (lastSaved?.status || batch?.status || "open") as string;
  const status = selectedStatus || savedStatus;
  const canAddItems = status === "open";
  const canAddKolli = status === "open";
  const hasPendingStatusChange = status !== savedStatus;

  const handleCreateBuyer = async () => {
    const name = newBuyerName.trim();
    if (!name) return;
    const ref = await addDoc(collection(db, "salesCustomers"), {
      name,
      contact: newBuyerContact.trim(),
      createdAt: serverTimestamp(),
      createdBy: user?.uid || null,
    });
    setNewBuyerName("");
    setNewBuyerContact("");
    setShowNewBuyer(false);
    handleUpdateBatch({
      buyerId: ref.id,
      buyerName: name,
      buyerContact: newBuyerContact.trim(),
    });
  };

  const handleUpdateBatch = (patch: Record<string, any>) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      setDirty(computeDirty(next, status, lastSaved));
      return next;
    });
  };

  const goNext = async () => {
    setStatusError(null);
    const missing: string[] = [];

    const batchSnapshot = { ...(lastSaved || batch || {}), ...form, status: selectedStatus };

    if (status === "sold") {
      if (!batchSnapshot.buyerId) missing.push("Välj köpare");
    }
    if (status === "shipped") {
      if (!batchSnapshot.trackingId) missing.push("Tracking ID");
    }
    if (status === "invoiced") {
      if (!batchSnapshot.invoiceNumber) missing.push("Fakturanummer");
      if (!batchSnapshot.invoiceTotal) missing.push("Faktura total");
    }
    if (status === "paid") {
      if (batchSnapshot.paymentReceived == null) missing.push("Mottaget belopp");
    }
    if (status === "credited") {
      if (batchSnapshot.creditAmount == null) missing.push("Kreditbelopp");
      if (!batchSnapshot.creditNoteNumber) missing.push("Kreditfaktura nr");
    }

    if (missing.length > 0) {
      setStatusError("Saknas: " + missing.join(", "));
      return;
    }

    // 1) Spara kollin (lägga till/ta bort) och map temp-id -> nya id
    const existing = originalKollinRef.current;
    const current = kollin;
    const toAdd = current.filter((k) => !existing.some((e) => e.id === k.id));
    const toDelete = existing.filter((e) => !current.some((k) => k.id === e.id));
    const tempMap: Record<string, string> = {};

    for (const k of toAdd) {
      if (k.id.startsWith("tmp-")) {
        const ref = await addDoc(collection(db, "salesBatches", batchId, "kollin"), {
          name: k.name || "",
          createdAt: serverTimestamp(),
          createdBy: user?.uid || null,
        });
        tempMap[k.id] = ref.id;
      } else {
        // redan befintligt ID (ska inte hända i toAdd men för säkerhets skull)
        tempMap[k.id] = k.id;
      }
    }

    for (const k of toDelete) {
      await deleteDoc(doc(db, "salesBatches", batchId, "kollin", k.id));
    }

    // 2) Uppdatera items (kolli-assign) med temp-mappning
    const mappedItems = items.map((it) => {
      const mappedId = tempMap[it.salesKolliId || ""] || it.salesKolliId || null;
      const name = mappedId ? (kolliNameById[mappedId] || it.salesKolliName || null) : null;
      return { ...it, salesKolliId: mappedId, salesKolliName: name };
    });

    // 2b) Uppdatera items med lås-guard: blockera om posten är låst av annan (icke-stale)
    const me = user?.email ?? user?.uid ?? null;
    try {
      for (const it of mappedItems) {
        const orig = originalItemsRef.current.find((o) => o.id === it.id);
        const prevKolli = orig?.salesKolliId || null;
        const nextKolli = it.salesKolliId || null;
        if (prevKolli !== nextKolli) {
          await runTransaction(db, async (tx) => {
            const ref = doc(db, "itInventory", it.id);
            const snap = await tx.get(ref);
            if (!snap.exists()) throw new Error("Posten finns inte längre.");
            const cur = snap.data() as any;
            const lockedBy = cur.lockedBy ?? null;
            const lockedAtMs = toMillis(cur.lockedAt);
            const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
            if (lockedBy && lockedBy !== me && !isStale) {
              throw new Error(`Kunde inte flytta kolli: posten är låst av ${lockedBy}.`);
            }

            tx.update(ref, {
              salesKolliId: nextKolli,
              salesKolliName: it.salesKolliName || null,
              updatedAt: serverTimestamp(),
            });
          });
        }
      }
    } catch (err: any) {
      const msg = err?.message || "Kunde inte flytta kolli p.g.a. lås eller fel.";
      alert(msg);
      setStatusError(msg);
      return; // avbryt sparsteget så vi inte får partiella flyttar
    }

    // 3) Spara batch meta/status
    const resolvedKollin = current.map((k) => ({
      ...k,
      id: tempMap[k.id] || k.id,
    }));

    await updateDoc(doc(db, "salesBatches", batchId), {
      ...form,
      status,
      kolliCount: resolvedKollin.length,
      updatedAt: serverTimestamp(),
    });

    // 4) Uppdatera lokal state och originalrefs
    setKollin(resolvedKollin);
    originalKollinRef.current = resolvedKollin;

    setItems(mappedItems);
    originalItemsRef.current = mappedItems;

    setDirty(false);
    setStatusError(null);
    setLastSaved((prev) => ({ ...(prev || (batch as any) || {}), ...form, status } as Batch));
  };

  const printKolli = (batchName: string, kolliName: string) => {
    const bn = (batchName || "").trim() || "Batch";
    const kn = (kolliName || "").trim() || "Kolli";
    const w = window.open("", "_blank", "width=1000,height=700");
    if (!w) return;
    w.document.write(`
      <html>
        <head>
          <style>
            @page { size: A4 landscape; margin: 15mm; }
            body { margin: 0; font-family: Arial, sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; }
            .wrap { text-align: center; width: 100%; }
            .line { font-size: 42pt; font-weight: 700; margin: 10mm 0; }
          </style>
        </head>
        <body>
          <div class="wrap">
            <div class="line">${bn}</div>
            <div class="line">${kn}</div>
          </div>
        </body>
      </html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };

  const PrintIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M7 5h10v4H7V5Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 15h10v4H7v-4Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 9H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h1" stroke="currentColor" strokeWidth="1.5" />
      <path d="M18 9h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 12h1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );

  const exportItemsToCsv = () => {
    const rows = [
      ["Serienr", "Tillverkare", "Modell", "Produkttyp", "Grade", "Kund", "CPU", "Lagring", "RAM", "Kolli", "Kommentar"],
      ...items.map((it) => [
        it.serial || "",
        it.manufacturer || "",
        it.model || "",
        it.productType || "",
        it.grade || "",
        it.customer || "",
        it.salesCpu || "",
        it.salesStorage || "",
        it.salesRam || "",
        kolliNameById[it.salesKolliId || ""] || it.salesKolliId || "",
        it.damageNotes || "",
      ]),
    ];
    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const v = String(cell ?? "");
            return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
          })
          .join(";")
      )
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName = `${(batch?.name || "batch").replace(/[^a-zA-Z0-9_-]+/g, "_")}.csv`;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const addKolli = (): void => {
    const nextIndex = (kollin.length || 0) + 1;
    const nextName = `Kolli ${nextIndex}`;
    const tempId = `tmp-${Date.now()}-${Math.round(Math.random() * 100000)}`;
    setKollin((prev) => [...prev, { id: tempId, name: nextName }]);
    setDirty(true);
  };

  const assignKolli = (itemId: string, kolliId: string | null) => {
    const name = kolliId ? (kolliNameById[kolliId] || null) : null;
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, salesKolliId: kolliId || null, salesKolliName: name } : it
      )
    );
    setDirty(true);
  };

  const handleKolliMouseDown = async (e: React.MouseEvent<HTMLSelectElement>, itemId: string) => {
    // Optimistiskt: kolla cached lock först
    const cached = isLockedByOther(itemId);
    if (cached.locked) {
      e.preventDefault();
      const msg = `Posten är låst av ${cached.lockedBy}. Vänta på att låset släpps.`;
      setStatusError(msg);
      alert(msg);
      return;
    }
    // Dubbelkolla live mot DB
    const fresh = await refreshLockForItem(itemId);
    if (fresh.locked) {
      e.preventDefault();
      const msg = `Posten är låst av ${fresh.lockedBy}. Vänta på att låset släpps.`;
      setStatusError(msg);
      alert(msg);
    }
  };

  const handleClose = () => {
    if (dirty) {
      const ok = window.confirm("Du har osparade ändringar. Stäng utan att spara?");
      if (!ok) return;
    }
    onClose?.();
  };

  const deleteKolli = async (kolliId: string) => {
    if (!kolliId) return;
    const ok = window.confirm("Ta bort kolli? Enheter flyttas till Oplacerad.");
    if (!ok) return;
    setKollin((prev) => prev.filter((k) => k.id !== kolliId));
    setItems((prev) =>
      prev.map((it) =>
        it.salesKolliId === kolliId
          ? { ...it, salesKolliId: null, salesKolliName: null }
          : it
      )
    );
    setDirty(true);
  };

  return (
    <section className="w-full max-w-[1240px] mx-auto pt-3 pb-4 px-1 md:px-0 h-full flex flex-col overflow-hidden">
      <header className="flex justify-between items-start gap-3 mb-4">
        <div className="grid gap-2">
          <h2 className="gw-h3 m-0">{batch.name || "Batch"}</h2>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setSelectedStatus(s);
                  setStatusError(null);
                  setDirty(computeDirty(form, s, lastSaved));
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  s === status
                    ? "bg-primary text-white border-primary shadow-sm"
                    : "bg-surface text-muted border-transparent hover:bg-surface-2 hover:text-text"
                }`}
              >
                {STATUS_LABELS[s] || s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="gw-btn gw-btn-secondary" onClick={handleClose} disabled={!onClose}>
            Stäng
          </button>
          <button
            className="gw-btn gw-btn-primary"
            onClick={goNext}
            disabled={!hasPendingStatusChange && !dirty}
            title={hasPendingStatusChange || dirty ? "" : "Inga ändringar att spara"}
          >
            Spara
          </button>
        </div>
      </header>
      {statusError && (
        <div className="text-red-600 mb-3 text-sm flex flex-wrap items-center gap-2">
          <span>{statusError}</span>
          <span className="text-xs text-muted">När låset släppts kan du klicka “Spara” igen.</span>
          <button
            type="button"
            className="gw-btn gw-btn-secondary gw-btn-sm"
            onClick={() => setStatusError(null)}
          >
            Rensa varning
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-4 items-start">
        <div className="gw-card p-3.5 bg-surface">
          <div className="font-semibold mb-2.5">Batchinfo</div>
          <div className="flex flex-wrap md:flex-nowrap gap-3 mb-3 items-end mt-1">
            <label className="flex-auto md:w-2/5">
              <div className="gw-form-label">Köpare</div>
              <select
                className="gw-input"
                value={form.buyerId ?? ""}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id === "__new__") {
                    setShowNewBuyer(true);
                    return;
                  }
                  const b = buyerMap[id];
                  handleUpdateBatch({
                    buyerId: id || null,
                    buyerName: b?.name || null,
                    buyerContact: b?.contact || null,
                  });
                  setShowNewBuyer(false);
                  setNewBuyerName("");
                  setNewBuyerContact("");
                }}
              >
                <option value="">Välj köpare</option>
                {buyers.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
                <option value="__new__">+ Lägg till ny köpare</option>
              </select>
            </label>

            {showNewBuyer && (
              <div className="flex-auto md:w-3/5 grid gap-1.5">
                <div className="gw-form-label">Ny köpare</div>
                <div className="flex gap-2 flex-wrap">
                  <input
                    type="text"
                    className="gw-input flex-1 min-w-[140px]"
                    placeholder="Namn"
                    value={newBuyerName}
                    onChange={(e) => setNewBuyerName(e.target.value)}
                  />
                  <input
                    type="text"
                    className="gw-input flex-1 min-w-[140px]"
                    placeholder="Kontaktinfo"
                    value={newBuyerContact}
                    onChange={(e) => setNewBuyerContact(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      className="gw-btn gw-btn-primary"
                      onClick={handleCreateBuyer}
                      disabled={!newBuyerName.trim()}
                    >
                      Spara
                    </button>
                    <button className="gw-btn gw-btn-secondary" onClick={() => { setShowNewBuyer(false); setNewBuyerName(""); setNewBuyerContact(""); }}>
                      Avbryt
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <label>
              <div className="gw-form-label">Tracking ID</div>
              <input
                className="gw-input"
                value={form.trackingId ?? ""}
                onChange={(e) => handleUpdateBatch({ trackingId: e.target.value })}
              />
            </label>
            <label>
              <div className="gw-form-label">Fakturanummer</div>
              <input
                className="gw-input"
                value={form.invoiceNumber ?? ""}
                onChange={(e) => handleUpdateBatch({ invoiceNumber: e.target.value })}
              />
            </label>
            <label>
              <div className="gw-form-label">Faktura total</div>
              <input
                type="number"
                className="gw-input"
                value={form.invoiceTotal ?? ""}
                onChange={(e) => handleUpdateBatch({ invoiceTotal: Number(e.target.value) || null })}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <label>
              <div className="gw-form-label">Mottaget belopp</div>
              <input
                type="number"
                className="gw-input"
                value={form.paymentReceived ?? ""}
                onChange={(e) => handleUpdateBatch({ paymentReceived: Number(e.target.value) || null })}
              />
            </label>
            <label>
              <div className="gw-form-label">Kreditbelopp</div>
              <input
                type="number"
                className="gw-input"
                value={form.creditAmount ?? ""}
                onChange={(e) => handleUpdateBatch({ creditAmount: Number(e.target.value) || null })}
              />
            </label>
            <label>
              <div className="gw-form-label">Kreditfaktura nr</div>
              <input
                className="gw-input"
                value={form.creditNoteNumber ?? ""}
                onChange={(e) => handleUpdateBatch({ creditNoteNumber: e.target.value })}
              />
            </label>
          </div>

          <label className="block mb-3">
            <div className="gw-form-label">Kreditorsak / notering</div>
            <textarea
              className="gw-input min-h-[80px]"
              value={form.creditReason ?? ""}
              onChange={(e) => handleUpdateBatch({ creditReason: e.target.value })}
            />
          </label>
        </div>

        <div className="gw-card p-3 bg-surface grid gap-2.5">
          <div className="flex justify-between items-center gap-2">
            <div className="font-semibold">Kollin</div>
            <button className="gw-btn gw-btn-secondary gw-btn-sm" onClick={addKolli}>Lägg till kolli</button>
          </div>
          <div className="grid gap-1.5">
            {items.filter((it) => !it.salesKolliId).length > 0 && (
              <div className="flex justify-between items-center gap-2 rounded-md px-2.5 py-1.5 bg-surface-2">
                <div className="flex gap-2 items-center">
                  <strong>Oplacerad</strong>
                  <span className="text-muted text-xs">({items.filter((it) => !it.salesKolliId).length} enheter)</span>
                </div>
              </div>
            )}
            {kollin.length === 0 && (
              <div className="text-muted text-sm">Inga kollin ännu.</div>
            )}
            {kollin.map((k) => {
              const count = items.filter((it) => it.salesKolliId === k.id).length;
              return (
                <div key={k.id} className="flex justify-between items-center gap-2 rounded-md px-2.5 py-1.5 bg-surface-2">
                  <div className="flex gap-2 items-center">
                    <strong>{k.name || k.id}</strong>
                    <span className="text-muted text-xs">({count} enheter)</span>
                  </div>
                  <div className="flex gap-1.5 items-center">
                    <button className="gw-btn gw-btn-ghost gw-btn-icon" title="Skriv ut etikett" onClick={() => printKolli(batch?.name || "", k.name || k.id)}><PrintIcon size={14} /></button>
                    <button className="gw-btn gw-btn-ghost text-xs text-red-600 hover:text-red-700" onClick={() => deleteKolli(k.id)} title="Ta bort kolli">Ta bort</button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-xs text-muted">Ta bort flyttar enheter till &quot;Oplacerad&quot;.</div>
        </div>
      </div>

      <div className="gw-card mt-3 bg-surface flex-1 min-h-0 overflow-hidden p-0">
        <div className="flex justify-between items-center mb-2 px-3 pt-3">
          <div className="font-semibold">Enheter i batch</div>
          <button className="gw-btn gw-btn-secondary gw-btn-sm" onClick={exportItemsToCsv}>Print to Excel</button>
        </div>
        <div className="gw-table-wrap gw-table-wrap--in-modal max-h-[55vh] overflow-y-auto px-3 pb-3 rounded-lg">
        <table>
          <thead>
            <tr>
              <th className="w-32">Serienr</th>
              <th className="w-32">Tillverkare</th>
              <th className="w-40">Modell</th>
              <th className="w-40">Produkttyp</th>
              <th className="w-24">Grade</th>
              <th className="w-32">Kund</th>
              <th className="w-24">CPU</th>
              <th className="w-24">Lagring</th>
              <th className="w-24">RAM</th>
              <th className="w-40">Kolli</th>
              <th className="w-24">Öppna</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center p-4 text-muted">
                  Inga enheter i batchen.
                </td>
              </tr>
            )}
            {items.map((it) => (
              <tr key={it.id}>
                <td>{it.serial || "-"}</td>
                <td>{it.manufacturer || "-"}</td>
                <td>{it.model || "-"}</td>
                <td>{it.productType || "-"}</td>
                <td>{it.grade || "-"}</td>
                <td>{it.customer || "-"}</td>
                <td>{it.salesCpu || "-"}</td>
                <td>{it.salesStorage || "-"}</td>
                <td>{it.salesRam || "-"}</td>
                <td>
                  <select
                    className="gw-select"
                    value={it.salesKolliId || ""}
                    onChange={(e) => assignKolli(it.id, e.target.value || null)}
                    onMouseDown={(e) => void handleKolliMouseDown(e, it.id)}
                    disabled={isLockedByOther(it.id).locked}
                    title={isLockedByOther(it.id).locked ? `Låst av ${isLockedByOther(it.id).lockedBy}` : undefined}
                  >
                    <option value="">Oplacerad</option>
                    {kollin.map((k) => (
                      <option key={k.id} value={k.id}>{k.name || k.id}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    className="gw-btn gw-btn-secondary gw-btn-sm"
                    onClick={() => onOpenItem?.(it.id)}
                    disabled={!onOpenItem}
                  >
                    Öppna
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </section>
  );
}
